import _ from 'lodash'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { LoaderCircle } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { AIIcon } from '@/components/icons/AIIcon'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import useImageMirroring from '../useImageMirroring'
import { Alert, AlertTitle } from '@/components/ui/alert'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import PromptHelper from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/PromptHelper'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import Library from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/ImageLibrary'
import RefineMediaDialog from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/RefineMediaDialog'
import GenerationProgress from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/GenerationProgress'
import { useImageGeneration } from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useImageGeneration'

const Footer = ({ handleNextNavigation, handleBackNavigation, disabled }) => {
    return (
        <div className="tw:w-full">
            <div className="tw:flex tw:justify-end tw:gap-4 tw:w-full">
                <Button variant="secondary" className="tw:w-[156px] tw:!text-sm" onClick={handleBackNavigation}>
                    Back
                </Button>
                <Button onClick={handleNextNavigation} disabled={disabled} className="tw:w-[156px] tw:!text-sm">
                    Next
                </Button>
            </div>
        </div>
    )
}

const AiAssetGeneration = () => {
    const {
        selectedFormats,
        prompt,
        isGenerating,
        generatedImages,
        selectedImages,
        setSelectedImages,
        showDeleteConfirmationModal,
        loading,
        generationProgress,
        setPrompt,
        handleFormatSelection,
        handleGenerate,
        handleStopGenerate,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
        replaceAsset,
        trackGenerating,
    } = useImageGeneration()
    const [refining, setRefining] = useState(null)
    const { campaignId, goToStep, registerCustomNavigation, clearCustomNavigation, campaignFormat, markStepAs, dataForStepper } =
        useWizard()

    const { isMirroring, mirrorImageAndNavigateToNextScreen } = useImageMirroring({
        images: generatedImages,
        selectedImages,
    })

    useEffect(() => markStepAs(0), [])

    const handleBackNavigation = () => {
        markStepAs(0)
        goToStep('uploadAssets')
    }

    useEffect(() => {
        const customNav = (
            <Footer
                handleBackNavigation={handleBackNavigation}
                handleNextNavigation={mirrorImageAndNavigateToNextScreen}
                disabled={isMirroring || selectedImages.length === 0}
            />
        )
        registerCustomNavigation(customNav)

        return () => clearCustomNavigation()
    }, [selectedImages, isMirroring])

    const handleGenerateClick = () => {
        handleGenerate()
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="aiTextToImage" />

            <div className="flex-grow-1 tw:px-6 tw:py-4 overflow-auto">
                <div>
                    <span className="tw:text-base tw:font-semibold">Generate Image from Text Prompt</span>

                    {/* Aspect Ratio */}
                    <div className="tw:mt-4">
                        <span className="tw:text-sm tw:font-semibold">Aspect Ratio</span>
                        <div className="tw:flex tw:space-x-8 tw:mt-4">
                            {['square', 'vertical', 'horizontal'].map((ratio) => (
                                <div key={ratio} className="tw:flex tw:items-center tw:gap-2">
                                    <Checkbox
                                        id={ratio}
                                        checked={selectedFormats[ratio]}
                                        onCheckedChange={() => handleFormatSelection(ratio)}
                                    />
                                    <Label htmlFor={ratio} className="tw:text-white tw:text-sm capitalize">
                                        {_.startCase(ratio)} (
                                        {ratio === 'square' ? '1:1' : ratio === 'vertical' ? '9:16' : '1.91:1'})
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Prompt */}
                    <div className="tw:mt-6 tw:max-w-[500px]">
                        <span className="tw:text-sm tw:font-semibold">Prompt</span>
                        <p className="tw:text-sm tw:mb-1 tw:mt-2">
                            For best results, describe the subject, background, lighting, and style.
                        </p>
                        <Textarea
                            placeholder="Describe the image, or add requirements such as a style or colours"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            className="tw:min-h-24 tw:mt-4 tw:bg-transparent tw:border-gray-600 tw:text-white tw:placeholder:text-gray-400 tw:focus:border-blue-400"
                        />
                        <PromptHelper
                            mediaType="IMAGE"
                            draft={prompt}
                            onSelect={setPrompt}
                            disabled={isGenerating}
                            leading={
                                isGenerating ? (
                                    <Button variant="destructiveSoft" onClick={handleStopGenerate}>
                                        Stop Generation
                                    </Button>
                                ) : (
                                    <Button
                                        variant="secondary"
                                        onClick={handleGenerateClick}
                                        disabled={!prompt.trim() || loading || isGenerating}
                                    >
                                        {isGenerating && (
                                            <LoaderCircle size={16} className="tw:text-white tw:animate-spin" />
                                        )}
                                        {!isGenerating && <AIIcon size={24} />}
                                        Generate
                                    </Button>
                                )
                            }
                        />
                    </div>

                    {isGenerating && generationProgress.total > 0 && (
                        <GenerationProgress progress={generationProgress} />
                    )}

                    {isMirroring && (
                        <Alert className="tw:text-left tw:mt-6">
                            <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                            <AlertTitle className="tw:text-left">Please wait while we process your images.</AlertTitle>
                        </Alert>
                    )}

                    <Library
                        onDelete={handleDelete}
                        onRefine={setRefining}
                        images={generatedImages}
                        showHeader={!isGenerating}
                        selectedImages={selectedImages}
                        handleConfirmDelete={handleConfirmDelete}
                        onClearSelection={() => setSelectedImages([])}
                        showDeleteConfirmationModal={showDeleteConfirmationModal}
                        onSelect={(id) => handleSelect(campaignFormat === 'carousel', id)}
                        handleDeleteConfirmationCancel={() => setShowDeleteConfirmationModal(false)}
                    />
                    {refining && (
                        <RefineMediaDialog
                            mediaType="IMAGE"
                            asset={refining}
                            campaignId={campaignId}
                            open
                            onClose={(pending) => {
                                setRefining(null)
                                if (pending) trackGenerating(pending, refining)
                            }}
                            onReplaced={(asset) => {
                                replaceAsset(refining, asset)
                                setRefining(null)
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default AiAssetGeneration
